import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { TrendingUp, Target, Wallet, BarChart2, ArrowUpRight, ArrowDownRight, Loader2, Plus, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Legend,
} from 'recharts';
import { strategicPillars, roiData, budgetForecastData, quarterlyPerformance } from '@/data/mockData';
import { StrategicPillar, ROIData, BudgetForecast } from '@/types';
import { useTenant } from '@/hooks/useTenant';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';
import { ImportedInitiativesList } from '@/components/strategy/ImportedInitiativesList';
import { ImportReviewStrip } from '@/components/shared/ImportReviewStrip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

interface QuarterlyPerformance {
  quarter: string;
  revenue: number;
  cost: number;
  profit: number;
}

const LEADERSHIP_ROLES = ['pmo', 'executive', 'tenant_admin', 'admin'];

// ============================================================
// Shared dashboard renderer — used by BOTH the demo tenant (fed the
// static mock arrays) and real tenants (fed data transformed from
// strategy_pillars/strategy_project_returns/strategy_budget_forecast/
// strategy_quarterly_performance). Keeping one render path guarantees
// the two experiences stay visually identical, per the "build it
// exactly the same way" requirement.
// ============================================================

interface StrategyDashboardProps {
  roiData: ROIData[];
  budgetForecastData: BudgetForecast[];
  strategicPillars: StrategicPillar[];
  quarterlyPerformance: QuarterlyPerformance[];
  manageSlots?: {
    pillars?: React.ReactNode;
    projectReturns?: React.ReactNode;
    budgetForecast?: React.ReactNode;
    quarterlyPerformance?: React.ReactNode;
  };
}

const StrategyDashboard = ({
  roiData,
  budgetForecastData,
  strategicPillars,
  quarterlyPerformance,
  manageSlots,
}: StrategyDashboardProps) => {
  const { t, currency } = useLocalization();
  const totalInvestment = roiData.reduce((sum, item) => sum + item.investment, 0);
  const totalReturns = roiData.reduce((sum, item) => sum + item.returns, 0);
  const averageROI = totalInvestment > 0 ? Math.round(((totalReturns - totalInvestment) / totalInvestment) * 100) : 0;

  const pillarsOnTrack = strategicPillars.filter(p => p.status === 'on-track').length;
  const pillarsAtRisk = strategicPillars.filter(p => p.status === 'at-risk').length;
  const pillarsCritical = strategicPillars.filter(p => p.status === 'critical').length;

  return (
    <div className="space-y-6 masira-fade-in">
      {/* Strategy KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StrategyKPICard
          title={t('Total Investment')}
          value={currency(totalInvestment, { compact: true })}
          subtitle={t('Across all projects')}
          icon={Wallet}
          trend="neutral"
        />
        <StrategyKPICard
          title={t('Total Returns')}
          value={currency(totalReturns, { compact: true })}
          subtitle={t('Revenue generated')}
          icon={TrendingUp}
          trend="up"
        />
        <StrategyKPICard
          title={t('Average ROI')}
          value={`${averageROI}%`}
          subtitle={t('Portfolio performance')}
          icon={BarChart2}
          trend={averageROI > 0 ? 'up' : 'down'}
          change={averageROI}
        />
        <StrategyKPICard
          title={t('Strategic Pillars')}
          value={`${pillarsOnTrack}/${strategicPillars.length}`}
          subtitle={t('{0} at risk, {1} critical', { 0: pillarsAtRisk, 1: pillarsCritical })}
          icon={Target}
          trend={pillarsCritical > 0 ? 'down' : 'up'}
        />
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ROI by Project */}
        <div className="masira-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{t('ROI by Project')}</h3>
              <p className="text-sm text-muted-foreground">{t('Investment vs Returns analysis')}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-primary" />
                  
                  {t('Investment')}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-[hsl(var(--success))]" />
                  
                  {t('Returns')}
                </span>
              </div>
              {manageSlots?.projectReturns}
            </div>
          </div>
          {roiData.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">{t('No project ROI recorded yet.')}</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={roiData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={true} vertical={false} />
                  <XAxis type="number" tickFormatter={(v) => currency(v, { compact: true })} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis dataKey="name" type="category" width={100} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [currency(value, { compact: true }), '']}
                  />
                  <Bar dataKey="investment" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} barSize={12} />
                  <Bar dataKey="returns" fill="hsl(var(--success))" radius={[0, 4, 4, 0]} barSize={12} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Budget Forecast */}
        <div className="masira-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{t('Budget Forecast')}</h3>
              <p className="text-sm text-muted-foreground">{t('Planned vs Actual vs Forecast')}</p>
            </div>
            {manageSlots?.budgetForecast}
          </div>
          {budgetForecastData.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">{t('No budget forecast recorded yet.')}</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={budgetForecastData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis tickFormatter={(v) => currency(v, { compact: true })} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number | null) => value ? [currency(value, { compact: true }), ''] : ['—', '']}
                  />
                  <Legend />
                  <Bar dataKey="planned" name="Planned" fill="hsl(var(--muted))" radius={[4, 4, 0, 0]} barSize={20} />
                  <Bar dataKey="actual" name="Actual" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} barSize={20} />
                  <Line type="monotone" dataKey="forecast" name="Forecast" stroke="hsl(var(--warning))" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Strategic Pillars */}
      <div className="masira-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">{t('Strategic Pillar Alignment')}</h3>
            <p className="text-sm text-muted-foreground">{t('Progress towards organizational objectives')}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[hsl(var(--success))]" />
                
                {t('On Track')}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[hsl(var(--warning))]" />
                
                {t('At Risk')}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-destructive" />
                
                {t('Critical')}
              </span>
            </div>
            {manageSlots?.pillars}
          </div>
        </div>
        {strategicPillars.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t('No strategic pillars defined yet.')}</p>
        ) : (
          <div className="space-y-4">
            {strategicPillars.map((pillar) => (
              <PillarCard key={pillar.id} pillar={pillar} />
            ))}
          </div>
        )}
      </div>

      {/* Quarterly Performance */}
      <div className="masira-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">{t('Quarterly Performance')}</h3>
            <p className="text-sm text-muted-foreground">{t('Revenue, Cost & Profit trends')}</p>
          </div>
          {manageSlots?.quarterlyPerformance}
        </div>
        {quarterlyPerformance.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">{t('No quarterly performance recorded yet.')}</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={quarterlyPerformance}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="quarter" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis tickFormatter={(v) => currency(v, { compact: true })} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [currency(value, { compact: true }), '']}
                />
                <Legend />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="hsl(var(--success))" fill="url(#revenueGradient)" strokeWidth={2} />
                <Area type="monotone" dataKey="profit" name="Profit" stroke="hsl(var(--primary))" fill="url(#profitGradient)" strokeWidth={2} />
                <Line type="monotone" dataKey="cost" name="Cost" stroke="hsl(var(--destructive))" strokeWidth={2} dot={{ fill: 'hsl(var(--destructive))' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

interface StrategyKPICardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ElementType;
  trend: 'up' | 'down' | 'neutral';
  change?: number;
}

const StrategyKPICard = ({ title, value, subtitle, icon: Icon, trend, change }: StrategyKPICardProps) => {
  const { tx } = useLocalization();
  return (
    <div className="masira-card masira-card-hover">
      <div className="flex items-start justify-between">
        <div className="p-2 rounded-lg bg-primary/10">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        {change !== undefined && (
          <div className={cn(
            'flex items-center gap-1 text-sm font-medium',
            trend === 'up' && 'text-[hsl(var(--success))]',
            trend === 'down' && 'text-destructive',
            trend === 'neutral' && 'text-muted-foreground'
          )}>
            {trend === 'up' && <ArrowUpRight className="w-4 h-4" />}
            {trend === 'down' && <ArrowDownRight className="w-4 h-4" />}
            {Math.abs(change)}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <h3 className="text-2xl font-bold text-foreground">{value}</h3>
        <p className="text-sm text-muted-foreground mt-1">{tx(title)}</p>
        <p className="text-xs text-muted-foreground/70 mt-0.5">{tx(subtitle)}</p>
      </div>
    </div>
  );
};

interface PillarCardProps {
  pillar: StrategicPillar;
}

const PillarCard = ({ pillar }: PillarCardProps) => {
  const { tx, t } = useLocalization();
  const progressPercent = pillar.target > 0 ? Math.round((pillar.progress / pillar.target) * 100) : 0;

  return (
    <div className="p-4 rounded-lg bg-secondary/30 border border-border/50 hover:border-border transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-foreground">{pillar.name}</h4>
            <span className={cn(
              'masira-badge',
              pillar.status === 'on-track' && 'masira-badge-success',
              pillar.status === 'at-risk' && 'masira-badge-warning',
              pillar.status === 'critical' && 'masira-badge-danger'
            )}>
              {tx(pillar.status.replace('-', ' '))}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{tx(pillar.description)}</p>
        </div>
        <div className="text-end">
          <span className="text-2xl font-bold text-foreground">{pillar.progress}%</span>
          <p className="text-xs text-muted-foreground">{t('of')} {pillar.target}{t('% target')}</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative h-2 bg-muted rounded-full overflow-hidden mb-3">
        {/* Target indicator */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-foreground/40 z-10"
          style={{ left: `${pillar.target}%` }}
        />
        {/* Progress */}
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            pillar.status === 'on-track' && 'bg-[hsl(var(--success))]',
            pillar.status === 'at-risk' && 'bg-[hsl(var(--warning))]',
            pillar.status === 'critical' && 'bg-destructive'
          )}
          style={{ width: `${pillar.progress}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {pillar.completedInitiatives}  {t('of')} {pillar.initiatives}  {t('initiatives completed')}
        </span>
        <span className={cn(
          'font-medium',
          progressPercent >= 100 && 'text-[hsl(var(--success))]',
          progressPercent >= 80 && progressPercent < 100 && 'text-foreground',
          progressPercent < 80 && 'text-[hsl(var(--warning))]'
        )}>
          {progressPercent}{t('% to target')}
        </span>
      </div>
    </div>
  );
};

// ============================================================
// Real-tenant data layer. None of ROI/returns/revenue/cost/monthly
// budget actuals exist in synced data (no connector observes
// revenue) — these are leadership inputs by design (see
// docs/superpowers/specs/2026-08-18-strategy-planning-module-design.md).
// Everyone on the tenant can view; only leadership roles can edit.
// ============================================================

interface DbPillar {
  id: string;
  name: string;
  description: string | null;
  status: string;
  progress_pct: number;
  target_pct: number;
}

interface DbProjectReturn {
  id: string;
  project_id: string;
  investment: number;
  returns: number;
  project_name: string;
}

interface DbBudgetForecastRow {
  id: string;
  month: string;
  planned: number;
  actual: number;
  forecast: number;
}

interface DbQuarterlyRow {
  id: string;
  quarter_label: string;
  quarter_start: string;
  revenue: number;
  cost: number;
}

interface ProjectOption {
  id: string;
  name: string;
}

const monthLabel = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short' });

const RealStrategyDashboard = () => {
  const { tenantId } = useAuth();
  const { role } = useUserRole();
  const canManage = LEADERSHIP_ROLES.includes(role || '');

  const [pillars, setPillars] = useState<DbPillar[]>([]);
  const [initiativeCounts, setInitiativeCounts] = useState<Record<string, { total: number; completed: number }>>({});
  const [projectReturns, setProjectReturns] = useState<DbProjectReturn[]>([]);
  const [budgetForecast, setBudgetForecast] = useState<DbBudgetForecastRow[]>([]);
  const [quarterly, setQuarterly] = useState<DbQuarterlyRow[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [pillarsRes, itemsRes, returnsRes, forecastRes, quarterlyRes, projectsRes] = await Promise.all([
      supabase.from('strategy_pillars').select('id, name, description, status, progress_pct, target_pct').order('display_order'),
      supabase.from('strategy_items').select('pillar_id, status').not('pillar_id', 'is', null),
      supabase.from('strategy_project_returns').select('id, project_id, investment, returns, projects_sync(name)'),
      supabase.from('strategy_budget_forecast').select('id, month, planned, actual, forecast').order('month'),
      supabase.from('strategy_quarterly_performance').select('id, quarter_label, quarter_start, revenue, cost').order('quarter_start'),
      supabase.from('projects_sync').select('id, name').order('name'),
    ]);

    if (!pillarsRes.error) setPillars((pillarsRes.data ?? []) as DbPillar[]);

    if (!itemsRes.error) {
      const counts: Record<string, { total: number; completed: number }> = {};
      for (const row of (itemsRes.data ?? []) as { pillar_id: string; status: string }[]) {
        if (!counts[row.pillar_id]) counts[row.pillar_id] = { total: 0, completed: 0 };
        counts[row.pillar_id].total += 1;
        if (row.status === 'completed') counts[row.pillar_id].completed += 1;
      }
      setInitiativeCounts(counts);
    }

    if (!returnsRes.error) {
      setProjectReturns(
        (returnsRes.data ?? []).map((r: { id: string; project_id: string; investment: number; returns: number; projects_sync: { name: string } | null }) => ({
          id: r.id,
          project_id: r.project_id,
          investment: r.investment,
          returns: r.returns,
          project_name: r.projects_sync?.name ?? 'Unknown project',
        })),
      );
    }

    if (!forecastRes.error) setBudgetForecast((forecastRes.data ?? []) as DbBudgetForecastRow[]);
    if (!quarterlyRes.error) setQuarterly((quarterlyRes.data ?? []) as DbQuarterlyRow[]);
    if (!projectsRes.error) setProjects((projectsRes.data ?? []) as ProjectOption[]);

    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const strategicPillarsData: StrategicPillar[] = useMemo(
    () =>
      pillars.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description ?? '',
        progress: p.progress_pct,
        target: p.target_pct,
        status: p.status as StrategicPillar['status'],
        initiatives: initiativeCounts[p.id]?.total ?? 0,
        completedInitiatives: initiativeCounts[p.id]?.completed ?? 0,
      })),
    [pillars, initiativeCounts],
  );

  const roiChartData: ROIData[] = useMemo(
    () =>
      projectReturns.map((r) => ({
        name: r.project_name,
        investment: r.investment,
        returns: r.returns,
        roi: r.investment > 0 ? Math.round(((r.returns - r.investment) / r.investment) * 100) : 0,
      })),
    [projectReturns],
  );

  const budgetChartData: BudgetForecast[] = useMemo(
    () =>
      budgetForecast.map((r) => ({
        month: monthLabel(r.month),
        planned: r.planned,
        actual: r.actual,
        forecast: r.forecast,
      })),
    [budgetForecast],
  );

  const quarterlyChartData: QuarterlyPerformance[] = useMemo(
    () =>
      quarterly.map((r) => ({
        quarter: r.quarter_label,
        revenue: r.revenue,
        cost: r.cost,
        profit: r.revenue - r.cost,
      })),
    [quarterly],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <StrategyDashboard
      roiData={roiChartData}
      budgetForecastData={budgetChartData}
      strategicPillars={strategicPillarsData}
      quarterlyPerformance={quarterlyChartData}
      manageSlots={
        canManage
          ? {
              pillars: <ManagePillarDialog onSaved={load} />,
              projectReturns: <ManageProjectReturnDialog projects={projects} onSaved={load} />,
              budgetForecast: <ManageBudgetForecastDialog onSaved={load} />,
              quarterlyPerformance: <ManageQuarterlyDialog onSaved={load} />,
            }
          : undefined
      }
    />
  );
};

// ---- Leadership management dialogs ----

const ManagePillarDialog = ({ onSaved }: { onSaved: () => void }) => {
  const { t } = useLocalization();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'on-track' | 'at-risk' | 'critical'>('on-track');
  const [progressPct, setProgressPct] = useState('0');
  const [targetPct, setTargetPct] = useState('100');

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const { error } = await supabase.from('strategy_pillars').insert({
      name: name.trim(),
      description: description.trim() || null,
      status,
      progress_pct: Number(progressPct) || 0,
      target_pct: Number(targetPct) || 100,
    });
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t('Pillar added'));
    setName(''); setDescription(''); setStatus('on-track'); setProgressPct('0'); setTargetPct('100');
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs"><Plus className="w-3.5 h-3.5 me-1" />  {t('Add pillar')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t('Add Strategic Pillar')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t('Name')}</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>{t('Description')}</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>{t('Status')}</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="on-track">{t('On track')}</SelectItem>
                  <SelectItem value="at-risk">{t('At risk')}</SelectItem>
                  <SelectItem value="critical">{t('Critical')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t('Progress %')}</Label><Input type="number" value={progressPct} onChange={(e) => setProgressPct(e.target.value)} /></div>
            <div><Label>{t('Target %')}</Label><Input type="number" value={targetPct} onChange={(e) => setTargetPct(e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t('Cancel')}</Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}  {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ManageProjectReturnDialog = ({ projects, onSaved }: { projects: ProjectOption[]; onSaved: () => void }) => {
  const { t } = useLocalization();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [investment, setInvestment] = useState('0');
  const [returns, setReturns] = useState('0');

  const handleSave = async () => {
    if (!projectId) return;
    setSaving(true);
    const { error } = await supabase.from('strategy_project_returns').upsert(
      { project_id: projectId, investment: Number(investment) || 0, returns: Number(returns) || 0 },
      { onConflict: 'tenant_id,project_id' },
    );
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t('Project ROI saved'));
    setProjectId(''); setInvestment('0'); setReturns('0');
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs"><Pencil className="w-3.5 h-3.5 me-1" />  {t('Set ROI')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t('Set Project ROI')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('Project')}</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger><SelectValue placeholder={t('Select a project')} /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('Investment')}</Label><Input type="number" value={investment} onChange={(e) => setInvestment(e.target.value)} /></div>
            <div><Label>{t('Returns')}</Label><Input type="number" value={returns} onChange={(e) => setReturns(e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t('Cancel')}</Button>
          <Button onClick={handleSave} disabled={saving || !projectId}>
            {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}  {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ManageBudgetForecastDialog = ({ onSaved }: { onSaved: () => void }) => {
  const { t } = useLocalization();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState('');
  const [planned, setPlanned] = useState('0');
  const [actual, setActual] = useState('0');
  const [forecast, setForecast] = useState('0');

  const handleSave = async () => {
    if (!month) return;
    setSaving(true);
    const { error } = await supabase.from('strategy_budget_forecast').upsert(
      { month: `${month}-01`, planned: Number(planned) || 0, actual: Number(actual) || 0, forecast: Number(forecast) || 0 },
      { onConflict: 'tenant_id,month' },
    );
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t('Budget forecast saved'));
    setMonth(''); setPlanned('0'); setActual('0'); setForecast('0');
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs"><Pencil className="w-3.5 h-3.5 me-1" />  {t('Set month')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t('Set Monthly Budget')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t('Month')}</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>{t('Planned')}</Label><Input type="number" value={planned} onChange={(e) => setPlanned(e.target.value)} /></div>
            <div><Label>{t('Actual')}</Label><Input type="number" value={actual} onChange={(e) => setActual(e.target.value)} /></div>
            <div><Label>{t('Forecast')}</Label><Input type="number" value={forecast} onChange={(e) => setForecast(e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t('Cancel')}</Button>
          <Button onClick={handleSave} disabled={saving || !month}>
            {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}  {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ManageQuarterlyDialog = ({ onSaved }: { onSaved: () => void }) => {
  const { t } = useLocalization();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const [quarterStart, setQuarterStart] = useState('');
  const [revenue, setRevenue] = useState('0');
  const [cost, setCost] = useState('0');

  const handleSave = async () => {
    if (!label.trim() || !quarterStart) return;
    setSaving(true);
    const { error } = await supabase.from('strategy_quarterly_performance').upsert(
      { quarter_label: label.trim(), quarter_start: quarterStart, revenue: Number(revenue) || 0, cost: Number(cost) || 0 },
      { onConflict: 'tenant_id,quarter_label' },
    );
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t('Quarterly performance saved'));
    setLabel(''); setQuarterStart(''); setRevenue('0'); setCost('0');
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs"><Pencil className="w-3.5 h-3.5 me-1" />  {t('Set quarter')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t('Set Quarterly Performance')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('Quarter label')}</Label><Input placeholder="Q1 2026" value={label} onChange={(e) => setLabel(e.target.value)} /></div>
            <div><Label>{t('Quarter start date')}</Label><Input type="date" value={quarterStart} onChange={(e) => setQuarterStart(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('Revenue')}</Label><Input type="number" value={revenue} onChange={(e) => setRevenue(e.target.value)} /></div>
            <div><Label>{t('Cost')}</Label><Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t('Cancel')}</Button>
          <Button onClick={handleSave} disabled={saving || !label.trim() || !quarterStart}>
            {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}  {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StrategyView = () => {
  const { isDemoTenant } = useTenant();
  // Bumped when an imported initiative gets a pillar, so the real
  // dashboard reloads its per-pillar initiative counts.
  const [dashboardKey, setDashboardKey] = useState(0);
  // Bumped when strategy proposals are approved or undone, so the
  // imported-initiatives list below the strip re-fetches.
  const [importKey, setImportKey] = useState(0);

  // The demo tenant keeps the full mock chart dashboard exactly as-is;
  // real tenants get the same dashboard fed from real strategy data.
  // Initiatives imported from documents sit above both until they are
  // assigned to a pillar; the list renders nothing when there are none.
  return (
    <div className="space-y-6">
      <ImportReviewStrip type="strategy" onChanged={() => setImportKey((k) => k + 1)} />
      <ImportedInitiativesList refreshKey={importKey} onAssigned={() => setDashboardKey((k) => k + 1)} />
      {isDemoTenant ? (
        <StrategyDashboard
          roiData={roiData}
          budgetForecastData={budgetForecastData}
          strategicPillars={strategicPillars}
          quarterlyPerformance={quarterlyPerformance}
        />
      ) : (
        <RealStrategyDashboard key={dashboardKey} />
      )}
    </div>
  );
};

export default StrategyView;
